// Portal Usage — retailer-portal behaviour analytics (admin view).
//
// Backend (migration 0036 + modules/retailer_analytics.js ingest):
//   GET /api/admin/analytics/usage?period=7d|30d|90d|all
//     → { totals, features[], pages[], retailers[] }
//
// Aggregate feature usage, top pages, and most-active retailers from the
// retailer_portal_analytics event log. Read-only. Informs the dashboard
// overhaul.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

type Period = "7d" | "30d" | "90d" | "all";

interface Totals { events: number; page_views: number; active_retailers: number }
interface FeatureRow { feature: string | null; uses: number; retailers: number }
interface PageRow { page: string | null; views: number }
interface RetailerRow { name: string; events: number }
interface UsageResponse {
  ok: boolean;
  period: Period;
  totals: Totals;
  features: FeatureRow[];
  pages: PageRow[];
  retailers: RetailerRow[];
}

const PERIODS: Array<{ k: Period; label: string }> = [
  { k: "7d", label: "7 days" },
  { k: "30d", label: "30 days" },
  { k: "90d", label: "90 days" },
  { k: "all", label: "All time" },
];

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (ch) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}

const page: Page = {
  name: "usage",
  label: "Portal Usage",
  icon: "📈",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    let ac = new AbortController();
    let period: Period = "30d";
    let loading = true;
    let error: string | null = null;
    let data: UsageResponse | null = null;

    const rows = <T,>(items: T[], cells: (r: T) => string[], empty: string): string =>
      items.length === 0
        ? `<tr><td class="ua-empty" colspan="3">${empty}</td></tr>`
        : items.map((r) => `<tr>${cells(r).map((c) => `<td>${c}</td>`).join("")}</tr>`).join("");

    function render(): void {
      const tabs = PERIODS.map((p) =>
        `<button type="button" class="ua-tab${p.k === period ? " is-active" : ""}" data-period="${p.k}">${p.label}</button>`,
      ).join("");

      let bodyHtml: string;
      if (loading) {
        bodyHtml = `<div class="ua-state">Loading usage…</div>`;
      } else if (error) {
        bodyHtml = `<div class="ua-state ua-state--err">${esc(error)}</div>`;
      } else if (!data) {
        bodyHtml = `<div class="ua-state">No data.</div>`;
      } else {
        const t = data.totals || { events: 0, page_views: 0, active_retailers: 0 };
        bodyHtml = `
          <div class="ua-kpis">
            <div class="ua-kpi"><div class="ua-kpi__val">${t.events.toLocaleString()}</div><div class="ua-kpi__lbl">Events</div></div>
            <div class="ua-kpi"><div class="ua-kpi__val">${t.page_views.toLocaleString()}</div><div class="ua-kpi__lbl">Page views</div></div>
            <div class="ua-kpi"><div class="ua-kpi__val">${t.active_retailers.toLocaleString()}</div><div class="ua-kpi__lbl">Active retailers</div></div>
          </div>
          <div class="ua-cols">
            <section class="ua-panel">
              <h2>Feature usage</h2>
              <table class="ua-table">
                <thead><tr><th>Feature</th><th>Uses</th><th>Retailers</th></tr></thead>
                <tbody>${rows(data.features, (r) => [esc(r.feature ?? "(untagged)"), String(r.uses), String(r.retailers)], "No feature events yet.")}</tbody>
              </table>
            </section>
            <section class="ua-panel">
              <h2>Top pages</h2>
              <table class="ua-table">
                <thead><tr><th>Page</th><th>Views</th></tr></thead>
                <tbody>${rows(data.pages, (r) => [esc(r.page ?? "(unknown)"), String(r.views)], "No page views yet.")}</tbody>
              </table>
            </section>
            <section class="ua-panel">
              <h2>Most active retailers</h2>
              <table class="ua-table">
                <thead><tr><th>Retailer</th><th>Events</th></tr></thead>
                <tbody>${rows(data.retailers, (r) => [esc(r.name), String(r.events)], "No activity yet.")}</tbody>
              </table>
            </section>
          </div>`;
      }

      container.innerHTML = `
        <section class="ua-root">
          <header class="ua-header">
            <div>
              <h1>Portal Usage</h1>
              <p class="ua-sub">How retailers use the portal — page views, feature usage and activity. Feeds the dashboard overhaul.</p>
            </div>
            <div class="ua-tabs">${tabs}</div>
          </header>
          ${bodyHtml}
        </section>`;

      container.querySelectorAll<HTMLButtonElement>("[data-period]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const p = btn.dataset.period as Period;
          if (p && p !== period) { period = p; void load(); }
        });
      });
    }

    async function load(): Promise<void> {
      ac.abort();
      ac = new AbortController();
      loading = true;
      error = null;
      render();
      try {
        const resp = await ctx.api<UsageResponse>(
          `/admin/analytics/usage?period=${period}`,
          { signal: ac.signal },
        );
        data = resp;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        error = err instanceof ApiError
          ? `Couldn't load usage (HTTP ${err.status})`
          : "Couldn't load usage data";
      } finally {
        loading = false;
        render();
      }
    }

    void load();
    return () => { ac.abort(); };
  },
};

registerPage(page);
